import {
  Form,
  Input,
  Link,
  Button,
  Space,
  Spin,
  Message,
} from "@arco-design/web-react";
import { FormInstance } from "@arco-design/web-react/es/Form";
import { IconLock, IconRefresh, IconUser } from "@arco-design/web-react/icon";
import React, { useCallback, useEffect, useRef, useState } from "react";
import useLocale from "@/utils/useLocale";
import request from "@/utils/request";
import { getAuthToken, setAuthToken } from "@/utils/token";
import locale from "./locale";
import styles from "./style/index.module.less";

interface LoginResponse {
  code: number;
  message?: string;
  data?: {
    token: string;
    head?: string;
  };
}

interface CaptchaResponse {
  code: number;
  message?: string;
  data?: {
    captcha: string;
    key: string;
  };
}

interface NeedCaptchaResponse {
  code: number;
  message?: string;
  data?: boolean;
}

export default function LoginForm() {
  const formRef = useRef<FormInstance>();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaKey, setCaptchaKey] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [needCaptcha, setNeedCaptcha] = useState(false);
  const needCaptchaRef = useRef(false);

  const t = useLocale(locale);

  const clearCaptchaInput = useCallback(() => {
    setCaptchaInput("");
    formRef.current?.setFieldsValue({ captcha: "" });
  }, []);

  const setNeedCaptchaValue = useCallback((value: boolean) => {
    needCaptchaRef.current = value;
    setNeedCaptcha(value);
  }, []);

  const checkNeedCaptcha = useCallback(
    async (username?: string) => {
      const name = (username || "").trim();
      if (!name) {
        setNeedCaptchaValue(false);
        return false;
      }
      try {
        const res = await request.get<NeedCaptchaResponse>(
          "/admin/needCaptcha",
          { params: { username: name } }
        );
        const value = res.code === 200 && res.data === true;
        setNeedCaptchaValue(value);
        return value;
      } catch {
        setNeedCaptchaValue(false);
        return false;
      }
    },
    [setNeedCaptchaValue]
  );

  const fetchCaptcha = useCallback(
    async (clearInput = true) => {
      setCaptchaLoading(true);
      setErrorMessage("");
      try {
        const res = await request.get<CaptchaResponse>("/admin/captcha");
        const { code, data, message } = res;
        if (code === 200 && data?.captcha && data?.key) {
          setCaptchaImage(data.captcha);
          setCaptchaKey(data.key);
          if (clearInput) {
            clearCaptchaInput();
          }
        } else {
          throw new Error(message || t["login.form.captcha.errMsg"]);
        }
      } catch (error) {
        setErrorMessage(
          (error as Error).message || t["login.form.captcha.errMsg"]
        );
      } finally {
        setCaptchaLoading(false);
      }
    },
    [clearCaptchaInput, t]
  );

  function afterLoginSuccess() {
    // 记录登录状态
    localStorage.setItem("userStatus", "login");
    // 跳转首页
    window.location.href = "/";
  }

  async function login(params) {
    setErrorMessage("");
    setLoading(true);
    try {
      const captchaValue =
        (
          params?.captcha ??
          captchaInput ??
          formRef.current?.getFieldValue("captcha")
        )?.trim() || "";

      // 仅当上一次登录失败时（needCaptcha）才校验验证码
      if (needCaptchaRef.current) {
        if (!captchaKey) {
          Message.error(t["login.form.captcha.errMsg"]);
          fetchCaptcha();
          return;
        }
        if (!captchaValue) {
          Message.error(t["login.form.captcha.errMsg"]);
          return;
        }
      }

      const payload = {
        ...params,
        captcha: captchaValue,
        key: captchaKey,
      };
      const res = await request.post<LoginResponse>(
        "/admin/loginWithCaptcha",
        payload
      );
      const { code, message, data } = res;
      if (code === 200 && data?.token) {
        const authToken = `${data.head || ""}${data.token}`;
        setAuthToken(authToken);
        afterLoginSuccess();
      } else {
        const fallbackMessage = message || t["login.form.login.errMsg"];
        setErrorMessage(fallbackMessage);
        Message.error(fallbackMessage);
        // 登录失败后需要验证码：重新检查并刷新验证码
        const required = await checkNeedCaptcha(params?.username);
        if (required) {
          clearCaptchaInput();
          fetchCaptcha();
        }
      }
    } catch (error) {
      setErrorMessage(t["login.form.login.errMsg"]);
      await checkNeedCaptcha(params?.username);
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  }

  function onSubmitClick() {
    formRef.current.validate().then((values) => {
      login(values);
    });
  }

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const userStatus = window.localStorage.getItem("userStatus");
    if (getAuthToken() && userStatus === "login") {
      window.location.replace("/");
      return;
    }
    checkNeedCaptcha(formRef.current?.getFieldValue("username"));
  }, [checkNeedCaptcha]);

  return (
    <div className={styles["login-form-wrapper"]}>
      <div className={styles["login-form-title"]}>{t["login.form.title"]}</div>
      <div className={styles["login-form-sub-title"]}>
        {t["login.form.title"]}
      </div>
      <div className={styles["login-form-error-msg"]}>{errorMessage}</div>
      <Form
        className={styles["login-form"]}
        layout="vertical"
        ref={formRef}
        initialValues={{ username: "", password: "", captcha: "" }}
      >
        <Form.Item
          field="username"
          rules={[{ required: true, message: t["login.form.userName.errMsg"] }]}
        >
          <Input
            prefix={<IconUser />}
            placeholder={t["login.form.userName.placeholder"]}
            onPressEnter={onSubmitClick}
            onChange={(value) => {
              checkNeedCaptcha(value);
            }}
          />
        </Form.Item>
        <Form.Item
          field="password"
          rules={[{ required: true, message: t["login.form.password.errMsg"] }]}
        >
          <Input.Password
            prefix={<IconLock />}
            placeholder={t["login.form.password.placeholder"]}
            onPressEnter={onSubmitClick}
          />
        </Form.Item>
        {needCaptcha && (
          <Form.Item
            field="captcha"
            rules={[
              { required: true, message: t["login.form.captcha.errMsg"] },
            ]}
          >
            <div className={styles["login-form-captcha"]}>
              <Input
                placeholder={t["login.form.captcha.placeholder"]}
                onPressEnter={onSubmitClick}
                maxLength={6}
                value={captchaInput}
                onChange={(value) => {
                  setCaptchaInput(value);
                  formRef.current?.setFieldsValue({ captcha: value });
                }}
              />
              <div className={styles["login-form-captcha-img"]}>
                {captchaLoading ? (
                  <Spin />
                ) : captchaImage ? (
                  <img
                    src={captchaImage}
                    alt={t["login.form.captcha.alt"]}
                    onClick={() => fetchCaptcha()}
                  />
                ) : (
                  <Button
                    type="text"
                    size="mini"
                    icon={<IconRefresh />}
                    onClick={() => fetchCaptcha()}
                  >
                    {t["login.form.refreshCaptcha"]}
                  </Button>
                )}
              </div>
            </div>
            <Button
              type="text"
              size="mini"
              className={styles["login-form-captcha-refresh"]}
              icon={<IconRefresh />}
              onClick={() => fetchCaptcha()}
            >
              {t["login.form.refreshCaptcha"]}
            </Button>
          </Form.Item>
        )}
        <Space size={16} direction="vertical">
          <div className={styles["login-form-password-actions"]}>
            <Link
              onClick={() =>
                Message.info(
                  t["login.form.forgetPassword.tip"] || "请联系管理员"
                )
              }
            >
              {t["login.form.forgetPassword"]}
            </Link>
          </div>
          <Button type="primary" long onClick={onSubmitClick} loading={loading}>
            {t["login.form.login"]}
          </Button>
        </Space>
      </Form>
    </div>
  );
}
